/** @module csi/def/Action */
define([
	"react",
	"react-dom/client",
	"dojo/_base/lang",
	"dojo/dom-class",
	"dijit/form/Button",
	"dijit/form/ToggleButton",
	"csi",
	"csi/_base/modules",
	"csi/_base/underscore",
	"csi/access/_base",
	"designSystem/components/icon/TurntableIcon",
	"csi/def",
	"csi/def/ActionFactory",
	"csi/def/Request",
	"csi/def/Rule",
	"csi/def/ViewRef",
	"csi/html",
	"csi/locale",
	"csi/reflection",
	"csi/state/rootStore",
	"csi/util",
	"csi/utility/defUtility",
	"csi/view/PluginFactory",
	"csi/widget/MessageDialog",
	"csi/xml",
	"csi/xml/Query"
], function(React, ReactDom, lang, domClass, Button, ToggleButton, csi, modules, _, access, TurntableIcon, def, ActionFactory, Request, Rule, ViewRef, html, locale, reflection, {routerStore}, util, defUtility, PluginFactory, MessageDialog, xml, Query) {

	html.registerDataAttribute("DATA_ACTION_TOGGLE", "action-toggle", Boolean);

	var ACTION = "Action";
	var CSS_MATERIAL_ICON_SIZE_PREFIX = "csi-material-icon-";

	var MATERIAL_ICON_SIZES = {
		arrow_downward: 19,		// move down
		arrow_upward: 19,		// move up
		close: 19				// remove, delete
	};

	const CONFIG_PARAMETERS = [
		"bReleaseHold",
		"classController",
		"classControllerParams",
		"confirmAction",
		"confirmIcon",
		"dialogCardId",
		"reportProgress",
		"requestParams",
		"returnParams",
		"validateActionFunction",
		"m_accessAction",
		"m_fixedQuery",
		"m_request",
		"m_preAction",
		"m_onAction",
		"m_postAction",
		"m_postActionError",
		"m_right",
		"m_rules",
		"m_viewRef"
	];

	const DOUBLE_CLICK_DELAY = 400;
	let _lastButton;
	let _lastButtonTime = 0;

	return ActionFactory.declare(ACTION, null, {
		// Boolean: indicates if action acts on view selections or current context
		//bActOnSelections: false,

		/** @property {boolean} bKeepViewEnabled - indicates that action should not disable the view when being applied */
		bKeepViewEnabled: false,

		// Boolean: indicates if changes from action should be published to the server immediately rather than held on the client waiting for more changes
		//bReleaseHold: false,

		// Boolean: indicates if validating action (on toolbar) should include normal access checks
		//bValidateWithAccess: false,

		// Boolean: when checking access to action, check view's read-only status rather than the Context's full read-only status (used to bypass frozen status of current node)
		//bCheckReadOnlyView: false,

		// Boolean: set to true to force an action into the ellipses menu when it would normally not be (i.e. Copy, Delete)
		//bEllipses: false,

		// String: the class name of derived csi.view.Widget to use as controller
		//classController: null,

		// Object: json string on the action xml to pass parameters for the controller to use
		//classControllerParams: null,

		// String: the class name of derived csi.view.ToolbarPlugin to use on toolbar
		//classToolbarPlugin: null,

		// Object: parameters to pass to csi.view.ToolbarPlugin
		//classToolbarPluginParams: null,

		// string: indicates primary button for Confirm action. There will be no confirmation if value is not specified.
		//confirmAction: null,

		// String[]: dependency paths for action
		//dependencyPaths: null,

		/** @property {string} dialogCardId - card to use in popup table dialog rather than the default card */
		dialogCardId: null,

		/* @property {string} iconButtonClass - css class for styling ActionIconButton */
		//iconButtonClass: null,

		// String: attribute id on which action is operating
		//idAttr: null,

		// String: text used to designate material font icon
		//materialIcon: null,

		// String: identifier for parent menu that will place this action in a sub-menu (applies only to actions under Toolbar Menu)
		//parentMenu: "",

		// DOMNode: used in conjunction with requirePrivateNode to keep a reference to the node. Used only with ACTION_ICON and table widget.
		//privateNode: null,

		// Boolean: indicates if report progress should be reported back to the client
		//reportProgress: false,

		// Boolean: flag to indicate that iconAction should be hidden and will be used in popup action menu in table row.
		// Used only with ACTION_ICON and table widget.
		//requirePrivateNode: null

		// Object: additional parameters to add to action request
		//requestParams: null,

		// Object: if exists, will force second query to get modified nodes from action rather than
		// using publish request to return all modified nodes
		returnParams: false,

		// String: explicit target class for action (usually this can be determined from view automatically)
		//target: null,

		/** 
		 * @property {boolean} testForRepetition - set to true if action cannot be repeated on same element immediately after finishing previous one;
		 * it is rare that this would ever need to be the case, but there have been several defects with the code handling this. The problem
		 * should be resolved now, but limiting exposure of this code in 7.5 to reduce risk.
		 */
		//testForRepetition: false,

		// String: indicates if action uses some context in creating data store query - used as key to data store
		//urlContext: null,

		// String|Function: function to use to validate action input (called before processing input)
		//validateActionFunction: null,

		//m_tag: null,
		//m_id: null,
		//m_name: null,
		//m_class: null,
		//m_path: null,
		//m_request: null,
		//m_right: null,
		//m_rightParent: null,
		//m_rules: null,
		//m_typeControl: null,

		//m_viewRef: null,
		//m_filtersView: null,
		//m_fixedQuery: null,

		//m_bSubmit: false,
		//m_tooltip: null,
		//m_rcTarget: null,

		// extension points
		//m_accessAction: null,
		//m_drawAction: null,
		//m_onAction: null,
		//m_preAction: null,
		//m_postAction: null,
		//m_postActionError: null,

		// toggle support - does not yet work with icons
		//m_nameToggle: null,

		// used to generate the key
		//m_additionalPrefixPath: null,

		// Relationship: relationship containing action (if any)
		//m_relationship: null,

		// derived actions can have mandatory rights in addition to configured ones
		//m_rightBase: null,
		//m_rightParentBase: null,

		// Boolean: indicates if action cannot be performed on dormant node
		//_bCheckDormant: false,

		// Boolean: indicates if action cannot be performed on dormant parent node
		//_bCheckDormantParent: false,

		// Number[]: array of node/attribute flags to check access against
		//_nodeFlags: null,
		//_attrFlags: null,


		constructor: function(/*XMLNode?*/xn) {
			if (!xn) return;

			var gp = util.getProperty;
			var params;
			var q;

			// required
			this.m_id = gp(xn, "Id");

			// optional
			this.m_class = gp(xn, "Class");
			this.m_path = gp(xn, "Path");
			this.m_request = defUtility.getDef(xn, "Request", (element) => new Request(element));
			this.m_right = gp(xn, "Right");									// [No right(default), Read, Write, Admin]
			this.m_rightParent = gp(xn, "ParentRight");						// [No right(default), Read, Write, Admin]
			this.m_rules = util.getDefs(xn, "Rule", Rule);				// rules that must pass for action to be valid
			this.m_typeControl = gp(xn, "Control");							// [Link(default), Button, Image]

			this.m_viewRef = util.getDef(xn, "ViewRef", ViewRef);		// open view to gather information for view
			this.m_filtersView = util.getElements(xn, "FilterView/Attribute");	// dynamic pre-set filters for view

			this.dependencyPaths = util.getProperties(xn, "DependencyPath", "Path");
			this.dialogCardId = util.getProperty(xn, "DialogCard");

			// set fixed filters on query
			var fixedFilters = util.getElements(xn, "Filter/child::*"); // fixed filters for view
			if (fixedFilters) {
				q = new Query();
				q.importElements(fixedFilters);
			}
			this.m_fixedQuery = q;

			this.m_accessAction = gp(xn, "AccessAction");
			this.m_drawAction = gp(xn, "DrawAction");
			this.m_onAction = gp(xn, "OnAction");
			this.m_preAction = gp(xn, "PreAction");
			this.m_postAction = gp(xn, "PostAction");
			this.validateActionFunction = gp(xn, "ValidateAction");

			this.confirmAction = gp(xn, "Confirm");
			if (this.confirmAction in [true, "true", 1, "1"]) {
				this.confirmAction = MessageDialog.BUTTON_YES;
			}
			this.confirmActionTG = gp(xn, "ConfirmTG");

			this.bReleaseHold = util.getBooleanProperty(xn, "ReleaseHold");
			this.classController = gp(xn, "ControllerClass");
			this.classControllerParams = csi.fromJson(gp(xn, "ControllerClassParams"));
			this.classToolbarPlugin = gp(xn, "PluginClass");
			this.modules = util.getProperty(xn, "Modules");
			this.notModules = util.getProperty(xn, "NotModules");
			this.parentMenu = util.getProperty(xn, "ParentMenu");
			this.target = gp(xn, "Target");
			this.reportProgress = util.getBooleanProperty(xn, "ReportProgress");

			if (params = gp(xn, 'Parameters')) {
				this.requestParams = csi.fromJson(params);
			}

			// read ReturnNodes request parameters in JSON format - if empty string, then override
			// any special action behavior with standard publish request to return nodes
			if (params = gp(xn, "ReturnParameters")) {
				this.returnParams = csi.fromJson(params);
			} else if (typeof params === "string") {
				this.returnParams = null;
			}

			// determine if material icon
			var materialIcon = gp(xn, "MaterialIcon");
			if (materialIcon) {
				this.setMaterialIcon(materialIcon);
			}

			// determine if there is ConfirmIcon
			var confirmIcon = gp(xn, "ConfirmIcon");
			if (confirmIcon) {
				this.setConfirmIcon(confirmIcon);
			}
		},

		assignFormIfNecessary: function(/*String*/ formId) {
			// summary: Allow the create node actions (New, Copy) to not use default form by setting Form parameter to empty string
			if (this.m_viewRef && (formId != null)) {
				console.error("Action cannot use both 'ViewRef' and 'Form' parameter!");
			} else if (!this.m_viewRef && (formId != "")) {
				this.m_viewRef = lang.mixin(new ViewRef(), {m_id: formId || def.DEFAULT_FORM_ID});
			}
		},

		isDerived: function() {
			// summary: this will work so long as Action remains base class
			return !!this.constructor.superclass;
		},

		isIcon: function() {
			// summary: return true if action will be rendered as icon
			return this.m_typeControl == def.ACTION_ICON;
		},

		isValid: function() {
			// summary: return true if action does not apply to currently configured modules
			return (!this.modules || modules.hasModules(this.modules)) &&
				(!this.notModules || !modules.hasModules(this.notModules));
		},

		hasSpecialLogic: function() {
			// summary: return true if custom javascript necessary for action or if action needs to be confirmed; used to
			// determine if action can be performed outside normal controller using special UI (table copy and paste, gallery drag and drop)
			return !!this.confirmAction ||
				this.m_preAction ||
				this.m_onAction ||
				this.m_postAction ||
				this.m_postActionError ||
				this.validateActionFunction ||
				this.classController;
		},

		/**
		 * Copy configuration properties from given action to this action
		 * @param {Action} [sourceAction]
		 */
		mixinConfigParameters(sourceAction) {
			if (sourceAction) {
				if (sourceAction.hasOwnId()) {
					this.m_id = sourceAction.m_id;
				}

				CONFIG_PARAMETERS.forEach((prop) => {
					if (sourceAction[prop]) {
						this[prop] = sourceAction[prop];
					}
				});
			}
		},

		setBaseRights: function(/*String?*/rightNode, /*String?*/rightParent) {
			// summary: some actions have default rights that they must satisfy, so reconcile those rights with explicitly configured rights
			var n;

			if (rightNode) {
				if (this.m_right == "") {
					// if right is explicitly disabled, then still need to check if node is dormant before allowing action
					this._bCheckDormant = true;
				} else {
					n = xml.rightIsMore(rightNode, this.m_right);
					if (n == 0) {
						// if base right supersedes configured right (if any), then just need to check for base right
						this.m_right = rightNode;
					} else if (n > 0) {
						// if base right is different than configured right, then need to check for both
						this.m_rightBase = rightNode;
					}
				}
			}

			// same as above, except for parent
			if (rightParent) {
				if (this.m_rightParent == "") {
					this._bCheckDormantParent = true;
				} else {
					n = xml.rightIsMore(rightParent, this.m_rightParent);
					if (n == 0) {
						this.m_rightParent = rightParent;
					} else if (n > 0) {
						this.m_rightParentBase = rightParent;
					}
				}
			}
		},

		setMaterialIcon: function(/*String*/s) {
			this.materialIcon = s;
			this.m_typeControl = def.ACTION_ICON;
		},

		setConfirmIcon: function(/*String*/confirmIcon) {
			this.confirmIcon = confirmIcon;
		},

		getId: function() {
			return this.m_id || this.m_tag || ACTION;
		},

		/**
		 * Returns label for action menu item
		 * @param {boolean} bToggled
		 * @return {string}
		 */
		getMenuItemLabel: function(bToggled) {
			return _.escape(this.getName(bToggled));
		},

		getRequestParameters() {
			let {reportProgress, requestParams} = this;

			if (reportProgress && !requestParams?.ProgressId) {
				requestParams = {
					...requestParams,
					"ProgressId": util.uuid()
				};
			}

			if (this.hasReturnParameters() && !requestParams?.ReturnNodes) {
				requestParams = {
					...requestParams,
					"ReturnNodes": "None"
				};
			}

			return requestParams;
		},

		getReturnParameters: function() {
			return this.returnParams || {};
		},

		hasOwnId: function() {
			// summary: return true if action has explicit id
			return this.m_id && (this.m_id != this.m_tag) && (this.m_id != ACTION);
		},

		hasReturnParameters: function() {
			return this.returnParams !== false;
		},

		setReturnParameters: function(/*Object?*/params) {
			// summary: set parameters used for request to get nodes modified by Action in separate request than publish;
			// if ReturnParameters explicitly set to empty (null), then do not override
			if (this.returnParams !== null) {
				this.returnParams = this.returnParams ? lang.mixin(params || {}, this.returnParams) : params || {};
			}
		},

		resolve: function(/*LocaleResolver*/lr, /*Class*/rcRoot, /*Node*/nodeRoot) {
			// summary: resolve action class and strings
			this.m_id = this.getId();
			this.m_typeControl = this.m_typeControl || def.ACTION_LINK;

			if (!this.m_rcTarget) {
				this.m_rcTarget = this.target ? reflection.getClass(this.target) : (rcRoot || reflection.getNodeClass(nodeRoot));

				if (this.m_rcTarget && this.m_path) {
					this.m_rcTarget = this.m_rcTarget.getPathClass(this.m_path);
				}
			}

			if (this.m_name == null) {
				lr = lr || locale.localeResolver;
				var obj = lr.resolveActionNames(this.getKey(), this.getTargetType(), this.m_id, this._getNameTag(lr), this._getNameParams(lr));

				if (this.m_typeControl == def.ACTION_TOGGLE_BUTTON && !obj.nameToggle) {
					obj.nameToggle = obj.name;
				}

				this.m_name = obj.name;
				this.m_nameToggle = obj.nameToggle;
				this.m_tooltip = this.m_tooltip || obj.tooltip;
			}
		},

		getCSS: function(/*Boolean?*/bToggled) {
			// summary: return CSS necessary for this action
			var classes = [html.CSS_ACTION];

			switch (this.m_typeControl) {
				case def.ACTION_ICON:
					classes.push(html.CSS_ACTION + this.m_typeControl);
					break;

				case def.ACTION_TOGGLE_BUTTON:
					classes.push("csi-button-switch");
					break;
			}

			if (this.materialIcon) {
				classes.push(html.CSS_MATERIAL_ICON);

				if (MATERIAL_ICON_SIZES[this.materialIcon]) {
					classes.push(CSS_MATERIAL_ICON_SIZE_PREFIX + MATERIAL_ICON_SIZES[this.materialIcon]);
				}
			}

			if (bToggled) {
				classes.push(html.CSS_ACTION + html.CSS_ACTION_TOGGLE);
			}

			if (this.m_typeControl != def.ACTION_TOGGLE_BUTTON) {
				if (this.m_class) {
					classes.push(this.m_class + (bToggled ? html.CSS_ACTION_TOGGLE : ""));
				} else if (this.m_typeControl == def.ACTION_BUTTON) {
					classes.push(this.m_bSubmit ? html.CSS_BUTTON_PRIMARY : html.CSS_BUTTON_SECONDARY);
				}
			}

			return classes.join(" ");
		},

		getIconCSS: function() {
			var classes = [html.CSS_ICON];

			if (this.materialIcon) {
				classes.push(html.CSS_MATERIAL_ICON);

				if (MATERIAL_ICON_SIZES[this.materialIcon]) {
					classes.push(CSS_MATERIAL_ICON_SIZE_PREFIX + MATERIAL_ICON_SIZES[this.materialIcon]);
				}
			}

			if (this.m_class) {
				classes.push(this.m_class);
			}

			return classes.join(" ");
		},

		getName: function(/*Boolean*/bToggle) {
			return bToggle ? (this.m_nameToggle || this.m_name) : this.m_name;
		},

		getDescription: function(/*Boolean*/bToggle) {
			return this.m_tooltip ? this.m_tooltip : this.getName(bToggle);
		},

		getKey: function() {
			// summary: return unique key for action
			var key = this.m_id;

			if (this.m_additionalPrefixPath) {
				key += "|" + this.m_additionalPrefixPath;
			}

			return key + (this.m_path ? "|" + this.m_path : "");
		},

		getString: function(/*String*/suffix, /*String[]?*/params) {
			// summary: get localied string for action id plus optional suffix
			const id = this.m_id;
			var type = this.getTargetType();
			var s;

			if (type) {
				s = locale.typeAction(type, id + suffix, params);
				if (s != null) return s;
			}

			s = locale.resourceIfExists(id + suffix, params);
			if (s != null) return s;

			if (this.m_tag && (this.m_tag != id)) {
				s = locale.resourceIfExists(this.m_tag + suffix, params);
				if (s != null) return s;
			}

			if (id != ACTION) {
				s = locale.resourceIfExists(ACTION + suffix, params);
				if (s != null) return s;
			}

			return suffix ? "" : id;
		},

		getTargetType: function() {
			// summary: return node type that this acts upon
			return this.m_rcTarget ? this.m_rcTarget.getType() : null;
		},

		canBeInlined: function() {
			// summary: by default, if action has ViewRef then do not allow it to be inline
			return !this.m_viewRef;
		},

		checkNodeAccess: function(/*Node*/node, /*Context*/ctx) {
			return this.m_right ?
				xml.nodeHasAccess(node, this.m_right, this.idAttr, this._nodeFlags, this._attrFlags)
				&& xml.nodeHasRight(node, this.m_rightBase) : true;
		},

		checkParentAccess: function(/*Node*/nodeParent, /*String*/idAttrParent, /*Context*/ctx) {
			return this.m_rightParent ?
				xml.nodeHasAccess(nodeParent, this.m_rightParent, idAttrParent)
				&& xml.nodeHasRight(nodeParent, this.m_rightParentBase) : true;
		},

		validate: function(/*Context*/ctx) {
			// summary: check whether action is valid for (shown in) given context - this is independent of whether or not user has access to perform it;
			// restrict if printing and action has rights
			if (ctx.bPrint && (this.m_right || this.m_rightParent)) {
				return false;
			}

			// remove action from toolbar if rules do not pass, unless not validating with rules (such as when in switchboard)
			if (this.bValidateWithAccess) {
				return this.hasAccess(ctx);
			}

			return !this.isHidden(ctx);
		},

		hasAccess: function(/*Context*/ctx) {
			// summary: check access to action based on given context -
			// path needs to be applied outside access check
			if (ctx.bStatic) {
				return false;
			}

			if (this.m_right) {
				// check access rights for node in context
				if (ctx.isReadOnly(this.bCheckReadOnlyView) && xml.rightIsMore(this.m_right, xml.RIGHT_READ) >= 0) {
					return false;
				} else if (!this.checkNodeAccess(ctx.getNode(), ctx)) {
					return false;
				}
			} else if (this._bCheckDormant && routerStore.locationIsDormant) {
				// actions that normally require write access should fail if node is dormant
				return false;
			}

			if (this.m_rightParent) {
				// check access rights for the parent
				if (ctx.isReadOnly(this.bCheckReadOnlyView) && xml.rightIsMore(this.m_rightParent, xml.RIGHT_READ) >= 0) {
					return false;
				} else if (!this.checkParentAccess(ctx.getParent(), ctx.getRelationId(), ctx)) {
					return false;
				}
			} else if (this._bCheckDormantParent && routerStore.locationIsDormant) {
				// actions that normally require write access on parent should fail if parent is dormant
				return false;
			}

			// check view access
			var viewAccess = ctx.view ? ctx.view.cachedAccessToAction(ctx, this) : access.RESOURCE_NO_ACCESS;

			switch (viewAccess) {
				case access.RESOURCE_NO_ACCESS:
					return false;

				case access.RESOURCE_READ_ONLY_ACCESS:
					// the view has read only access but this action requires
					// (either for itself or for the parent) right more than read (e.g. write or admin)
					if (xml.rightIsMore(this.m_right, xml.RIGHT_READ) >= 0 ||
						xml.rightIsMore(this.m_rightParent, xml.RIGHT_READ) >= 0) {
						return false;
					}

				// fall-through to FULL

				case access.RESOURCE_FULL_ACCESS:
					// if roles are configured for access to action, then disallow if in popup and read-only context;
					// in some cases action does not set Right or ParentRight, but only requires roles, and in
					// those cases we should disable action if in popup. This does not apply to read-only views in general
					// because in some cases we want to allow toolbar actions.
					if (ctx.bPopup && ctx.isReadOnly()) {
						return false;
					}

					break;
			}

			// check rules - if node not defined, then use parent node
			if (!def.processRules(ctx, this.m_rules)) return false;

			// finally, check special access function
			var f = csi.resolveFunction(this.m_accessAction, ctx.view);
			if (f && !f(ctx, this)) return false;

			return true;
		},

		hasOwnContext: function() {
			// summary: indicates if action element will contain own context, or rely on another element;
			// currently only used for tooltip actions where the context is stored in the element which is source of tooltip
			return !!this.m_path;
		},

		isHidden: function(/*Context*/ctx) {
			// summary: return true if action has been hidden through configuration
			return access.isResourceHidden(ctx.defView.getTypeNode(), ctx.defView.getId(), this.getKey());
		},

		toHtml: function(/*Context*/ctx, /*DOMNode*/refNode, /*Boolean*/bRenderDisabled, /*Boolean*/bForceDisabled) {
			// summary: determine if action is permitted before rendering
			var res = null;

			if (!ctx) {
				// handle special cases with page actions outside any view
				res = this.toHtmlWithAccess(ctx, refNode);
			} else if (ctx.push(this.m_path)) {
				// check for access to action before rendering
				if (!bForceDisabled && this.hasAccess(ctx)) {
					res = this.toHtmlWithAccess(ctx, refNode);
				}

				// if no access to action and want to render disabled actions, then do so now
				if (!res && bRenderDisabled) {
					res = this.toHtmlWithoutAccess(ctx, refNode);
				}

				ctx.pop();
			}

			return res;
		},

		toHtmlWithAccess: function(/*Context*/ctx, /*DOMNode*/refNode) {
			// render action
			var bToggled = this.isToggledInitially(ctx);
			var e, f;

			if (this.classToolbarPlugin) {
				e = PluginFactory.create({
					"class": html.CSS_ACTION,
					defAction: this,
					pluginName: this.m_id
				}, ctx);

				if (e) {
					e.placeAt(refNode);
					e.startup();
				}
			} else {
				if (f = csi.resolveFunction(this.m_drawAction, ctx.view)) {
					e = f(ctx, this, refNode, bToggled);
				} else {
					e = this.toHtmlControl(ctx, refNode, bToggled);
				}

				if (e) {
					ctx.applyActionContext(e, this.m_id);
				}
			}

			return e;
		},

		toHtmlWithoutAccess: function(/*Context*/ctx, /*DOMNode*/refNode) {
			// summary: render action as disabled
			var e = this.toHtmlControl(ctx, refNode);
			domClass.add(e, "dijitDisabled");
			return e;
		},

		toHtmlControl: function(/*Context*/ctx, /*DOMNode*/refNode, /*Boolean*/bToggled, /*Boolean*/bTurntable) {
			const {materialIcon} = this;
			var name = bToggled ? this.m_nameToggle : this.m_name;
			var tooltip = this.getDescription(bToggled) || "";
			var css = this.getCSS(bToggled);
			var e;
			var automationPrefix;

			switch (this.m_typeControl) {
				case def.ACTION_TOGGLE_BUTTON: {
					automationPrefix = "toggle";
					var toggleButton = new ToggleButton({
						"class": css,
						showLabel: true,
						checked: this.isToggledInitially(ctx),
						label: name,
						title: (name == tooltip) ? "" : tooltip
					});

					toggleButton.placeAt(refNode);
					toggleButton.startup();
					e = toggleButton.domNode;
					break;
				}

				case def.ACTION_BUTTON: {
					automationPrefix = "button";
					var button = new Button({
						"class": css,
						label: name,
						onClick: function(event) {
							// stop events from firing if being clicked multiple times
							const time = new Date().getTime();

							if ((_lastButton === this) && (time - _lastButtonTime < DOUBLE_CLICK_DELAY)) {
								event.stopPropagation();
								event.preventDefault();
							}

							_lastButton = this;
							_lastButtonTime = time;
						},
						title: (name == tooltip) ? "" : tooltip,
						type: this.m_bSubmit ? "submit" : "button"
					});
					button.placeAt(refNode);
					button.startup();
					e = button.domNode;
					break;
				}

				case def.ACTION_ICON_BUTTON: {
					automationPrefix = "button";
					var container = html.create("span", {
						"class": "csi-action-icon-btn"
					}, refNode);
					var div = html.create("div", {
						"class": "csi-action-icon-btn-inner " + (this.iconButtonClass || "csi-table-group-secondary-btn"),
						title: tooltip
					}, container);
					html.create("span", {
						"class": css,
						innerText: materialIcon,
					}, div);

					e = container;
					break;
				}

				case def.ACTION_ICON: {
					automationPrefix = "icon";
					if (bTurntable) {
						e = html.create("span", {
							"class": css + " csi-turntable-image-icon",
							title: tooltip
						}, refNode);

						const root = ReactDom.createRoot(e);
						const reactElement = React.createElement(TurntableIcon.default);
						root.render(reactElement);
					} else if (materialIcon) {
						e = html.create("span", {
							"class": css,
							innerText: materialIcon,
							title: tooltip
						}, refNode);
					} else {
						// if not a material icon, then it could be a FontAwesome icon; if so, this element will later be replaced
						// with an SVG element which will cause problems with the "privateNode" functionality. For that reason,
						// we need to create a wrapper for the element
						e = html.create("span", {
							title: tooltip
						}, refNode);
						html.create("span", {"class": css}, e);
					}

					break;
				}

				default: {
					automationPrefix = "button";
					e = html.create("a", {
						"class": css,
						innerText: name,
						title: (name == tooltip) ? "" : tooltip
					}, refNode);
					break;
				}
			}

			// hide actions, that will be added to popup menu
			if (this.requirePrivateNode) {
				html.setVisible(e, false);
				this.privateNode = e;
			}

			// toggle actions only supported for link/button actions
			if (this.m_nameToggle) {
				html.setDataAttribute(e, html.DATA_ACTION_TOGGLE, bToggled);
			}

			const viewId = ctx.defView?.m_id;
			const dataCsiAutomation = viewId ? `${automationPrefix}-${viewId}-${this.m_id}` : undefined;
			html.setDataAttribute(e, html.DATA_AUTOMATION, dataCsiAutomation);

			return e;
		},

		isToggledInitially: function(/*Context*/ctx) {
			// summary: determine if action should be in toggled state initially;
			// return value of null means that there is no way to tell from context, so assume answer is no
			return this.m_nameToggle ? null : false;
		},

		isToggled: function(/*DOMNode*/refNode) {
			return html.getDataAttribute(refNode, html.DATA_ACTION_TOGGLE);
		},

		toggleControl: function(/*DOMNode*/refNode) {
			const bToggleButton = (this.m_typeControl === def.ACTION_TOGGLE_BUTTON);

			if (bToggleButton || this.m_nameToggle) {
				var bToggle = !this.isToggled(refNode);
				var name = bToggle ? this.m_nameToggle : this.m_name;

				html.setDataAttribute(refNode, html.DATA_ACTION_TOGGLE, bToggle);

				if (this.m_typeControl == def.ACTION_BUTTON) {
					refNode.setAttribute("value", name);
				} else if (!bToggleButton) {
					// we don't want text of toggle buttons to change
					html.setInnerText(refNode, name);
				}
			}
		},

		_getNameParams: function(/*csi.LocaleResolver*/lr) {
			// summary: return array of parameters used to help name action
			return null;
		},

		_getNameTag: function(/*csi.LocaleResolver*/lr) {
			// summary: return array of parameters used to help name action
			return this.m_tag;
		}
	});
});
